/**
 * Native push-notification wiring.
 *
 * Flow:
 *
 *   1. On login (or first launch with a stored session) call
 *      `registerNativePush()`. It creates the Android notification
 *      channel, requests OS-level permission, hands that decision back
 *      as a boolean, and asks APNs/FCM for a device token.
 *
 *   2. The token is POSTed to `/push/token` so the API can target this
 *      install from `notify()` / `fanOutMessagePush()`.
 *
 *   3. Foreground notification taps dispatch a custom DOM event the
 *      router listens for, navigating to the deep-link in the payload.
 *
 * Web push continues to use `firebase/messaging` — this module is a
 * native-only complement. No-ops on web.
 *
 * Server contract:
 *   POST /push/token
 *   { token: string, platform: "WEB" | "ANDROID" | "IOS" }
 *
 * The endpoint is idempotent on (userId, token) — calling it again on
 * relaunch is safe and refreshes `updatedAt`.
 */
import {
  PushNotifications,
  type Token,
  type PushNotificationSchema,
  type ActionPerformed,
} from "@capacitor/push-notifications";
import { FCM } from "@capacitor-community/fcm";
import { App } from "@capacitor/app";
import { apiPost } from "@/api/client";
import { getPlatform, isNative } from "./platform";

export interface PushPermissionResult {
  granted: boolean;
  reason: "user-decision" | "unsupported" | "error";
}

/**
 * Last token we successfully handed to the server, so a second
 * `registerNativePush()` (e.g. the user logs out and back in as somebody
 * else) can re-associate the device with the new account even though the
 * OS won't fire a fresh `registration` event for an already-registered
 * install. Without this, the second user on a shared device never gets a
 * PushToken row.
 */
let lastKnownToken: string | null = null;

/**
 * The token this install was last handed by APNs/FCM, whether or not the
 * POST succeeded. Sign-out sends it to `POST /auth/logout` so the API can
 * retire this device for the account that is leaving (CAMP-2026-0833).
 */
let currentDeviceToken: string | null = null;

/** This device's native push token, or null on web / before registration. */
export function getNativeDevicePushToken(): string | null {
  return currentDeviceToken;
}

async function postDeviceToken(token: string): Promise<void> {
  const platform = getPlatform();
  if (platform === "web") return; // web FCM registers via firebase/messaging directly
  currentDeviceToken = token;
  try {
    await apiPost("/push/token", {
      token,
      platform: platform === "ios" ? "IOS" : "ANDROID",
    });
    lastKnownToken = token;
  } catch (err) {
    // Not fatal — the next login/relaunch retries. But do NOT swallow it
    // silently: a 404/401 here means the device is invisible to every push
    // the platform sends, and the only symptom users can report is
    // "notifications don't work", which is indistinguishable from a dozen
    // other causes. Route is `/push/token`; posting to `/devices` (which
    // does not exist) is how this broke the first time.
    console.error("[push] POST /push/token failed", err);
  }
}

/**
 * Resolve the FCM registration token on iOS.
 *
 * `@capacitor/push-notifications` hands us the raw APNs token; FCM needs
 * its own token, which `FCM.getToken()` mints — but only once
 * FirebaseMessaging has been given the APNs token. AppDelegate sets
 * `Messaging.messaging().apnsToken` explicitly before posting the
 * Capacitor notification, so by the time we get here it is normally
 * ready; the retry covers the swizzling race on older Firebase SDKs and
 * the brief window while APNs finishes handshaking.
 *
 * Returns null rather than falling back to the APNs token. That fallback
 * used to exist and was actively harmful: the raw APNs hex string is not
 * a valid FCM registration token, so it was stored in `PushToken`, made
 * the row look healthy, and then failed on every single send.
 */
async function resolveIosFcmToken(): Promise<string | null> {
  const DELAYS_MS = [0, 400, 1200, 3000];
  for (const delay of DELAYS_MS) {
    if (delay > 0) await new Promise((r) => setTimeout(r, delay));
    try {
      const { token } = await FCM.getToken();
      if (token) return token;
    } catch (err) {
      console.warn(`[push] FCM.getToken() failed (retry in ${delay}ms)`, err);
    }
  }
  console.error(
    "[push] could not obtain an FCM token on iOS. Usual cause: the build was " +
      "signed with the wrong aps-environment entitlement, or " +
      "GoogleService-Info.plist is missing from the app bundle.",
  );
  return null;
}

/**
 * Create the Android notification channel.
 *
 * Deliberately separate from — and called before — the permission
 * prompt: creating a channel needs no permission, and the channel has to
 * exist before the first notification arrives, not after the user
 * happens to accept a dialog. The FCM payload targets channel "default",
 * and AndroidManifest declares the same id as
 * `com.google.firebase.messaging.default_notification_channel_id` so a
 * cold install is covered even before this runs.
 */
async function ensureAndroidChannel(): Promise<void> {
  if (getPlatform() !== "android") return;
  try {
    await PushNotifications.createChannel({
      id: "default",
      name: "Default Notifications",
      description: "Default channel for school updates and alerts",
      importance: 5,
      visibility: 1,
      sound: "default",
      vibration: true,
    });
  } catch (err) {
    console.warn("[push] createChannel failed", err);
  }
}

/**
 * Request permission and register for remote notifications.
 *
 * Safe to call multiple times — after the first grant the OS short-
 * circuits subsequent calls, and we re-POST the cached token so a
 * different user signing in on the same device is targetable. Always
 * returns `{ granted: false, reason: "unsupported" }` on web.
 */
export async function registerNativePush(): Promise<PushPermissionResult> {
  if (!isNative()) return { granted: false, reason: "unsupported" };

  try {
    await ensureAndroidChannel();

    // `checkPermissions` first so an already-granted install doesn't
    // depend on requestPermissions() reporting correctly.
    let receive = (await PushNotifications.checkPermissions()).receive;
    if (receive === "prompt" || receive === "prompt-with-rationale") {
      receive = (await PushNotifications.requestPermissions()).receive;
    }
    if (receive !== "granted") {
      return { granted: false, reason: "user-decision" };
    }

    // `register()` is the call that asks Apple/Google for the device
    // token. The response arrives async via the `registration` event
    // in installNativePushHandlers, not as a return value.
    await PushNotifications.register();

    // Re-bind an already-minted token to whoever is logged in now. The OS
    // will not re-fire `registration` for an install it has already
    // registered, so without this the second account on a device never
    // lands a PushToken row.
    if (lastKnownToken) void postDeviceToken(lastKnownToken);

    return { granted: true, reason: "user-decision" };
  } catch (err) {
    console.error("[push] registerNativePush failed", err);
    return { granted: false, reason: "error" };
  }
}

/**
 * Reset the iOS app-icon badge once the person is looking at the app.
 *
 * The number on the icon is set by each push (`aps.badge`, the recipient's
 * unread bell count — see apps/api/src/lib/dispatch.ts) and iOS never lowers
 * it on its own, so without this the icon kept its last number (it used to
 * be a hard-coded "1") long after everything was read. The Capacitor plugin's
 * `removeAllDeliveredNotifications()` clears the delivered banners AND sets
 * `applicationIconBadgeNumber = 0`; the in-app bell carries the live count,
 * and the next push sets the icon to the real unread number again.
 *
 * iOS only: on Android the launcher badge follows the tray, and clearing the
 * tray on every open would change behaviour Android users already rely on.
 */
export async function clearAppIconBadge(): Promise<void> {
  if (getPlatform() !== "ios") return;
  try {
    await PushNotifications.removeAllDeliveredNotifications();
  } catch (err) {
    console.warn("[push] could not clear the app icon badge", err);
  }
}

/**
 * Subscribe to push events. Returns a teardown function so the caller
 * (typically `main.tsx`) can remove handlers on HMR.
 */
export async function installNativePushHandlers(opts: {
  onDeepLink: (path: string, schoolId?: string) => void;
}): Promise<() => void> {
  if (!isNative()) return () => {};

  const cleanups: Array<{ remove: () => Promise<void> }> = [];

  // The app is open: the icon badge has done its job (cold launch here,
  // and every return to the foreground below).
  void clearAppIconBadge();
  cleanups.push(
    await App.addListener("appStateChange", ({ isActive }) => {
      if (isActive) void clearAppIconBadge();
    }),
  );

  // Step 2 (token landed): POST it to the API.
  cleanups.push(
    await PushNotifications.addListener("registration", async (t: Token) => {
      if (getPlatform() === "ios") {
        const fcmToken = await resolveIosFcmToken();
        if (fcmToken) void postDeviceToken(fcmToken);
        return;
      }
      void postDeviceToken(t.value);
    }),
  );

  // Registration error — log to the console; surfacing a toast on every
  // launch would be noisy. Sentry picks it up via the global handler.
  cleanups.push(
    await PushNotifications.addListener("registrationError", (err) => {
      console.error("[push] registrationError", err);
    }),
  );

  // Foreground notification — let the OS show the banner (presentation
  // options in `capacitor.config.ts`), but also fan it out through our
  // in-app toast system so the user sees the same message inside the
  // app shell.
  cleanups.push(
    await PushNotifications.addListener(
      "pushNotificationReceived",
      (n: PushNotificationSchema) => {
        window.dispatchEvent(
          new CustomEvent("chai:push-received", { detail: n }),
        );
      },
    ),
  );

  // Tap action — navigate to the deep link the payload carries. We
  // never trust the URL to point off-app: the router-level guard
  // strips the origin in `setupDeepLinks` upstream.
  cleanups.push(
    await PushNotifications.addListener(
      "pushNotificationActionPerformed",
      (a: ActionPerformed) => {
        const deepLink =
          (a.notification.data?.deepLink as string | undefined) ??
          (a.notification.data?.url as string | undefined);
        // The API stamps every push with the notification's schoolId so a
        // multi-school overseer can be scoped to that campus first.
        const schoolId = a.notification.data?.schoolId as string | undefined;
        if (deepLink) opts.onDeepLink(deepLink, schoolId || undefined);
      },
    ),
  );

  return () => {
    cleanups.forEach((c) => {
      void c.remove();
    });
  };
}
